import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActivityPicker, AvailabilityEditor } from "@/components/member";
import { PageLoader, formatDate, formatTime } from "@/components/common";
import { Progress } from "@/components/ui/progress";
import { PartyPopper, Users, ArrowRight, Check, Frown, Vote, Sparkles } from "lucide-react";

const RADII = [5, 10, 15, 25, 50];

export default function Discover() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const groupParam = params.get("group");

    const [acts, setActs] = useState(null);
    const [myAvail, setMyAvail] = useState([]);
    const [defaultRadius, setDefaultRadius] = useState(25);
    const [view, setView] = useState("wizard"); // wizard | result | group
    const [step, setStep] = useState(0);
    const [selected, setSelected] = useState(new Set());
    const [windows, setWindows] = useState([]);
    const [radius, setRadius] = useState(25);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [group, setGroup] = useState(null);
    const [ranking, setRanking] = useState([]);
    const [capNudge, setCapNudge] = useState(null); // outing_cap detail
    const [membership, setMembership] = useState(null);

    const handleCap = (e) => {
        const d = e.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "outing_cap") { setCapNudge(d); return true; }
        return false;
    };

    useEffect(() => {
        api.get("/activities").then((r) => setActs(r.data));
        api.get("/me/full").then((r) => {
            setMyAvail(r.data.availability);
            setWindows(r.data.availability.map((w) => ({ date: w.date, start: w.start, end: w.end, tz: w.tz })));
            setSelected(new Set((r.data.preferences || []).map((p) => p.activity_id)));
            setDefaultRadius(r.data.user.travel_radius || 25);
            setRadius(r.data.user.travel_radius || 25);
        });
        api.get("/me/membership").then((r) => setMembership(r.data)).catch(() => {});
        if (groupParam) loadGroup(groupParam);
    }, [groupParam]);

    const loadGroup = async (gid) => {
        try {
            const { data } = await api.get(`/groups/${gid}`);
            setGroup(data);
            setView("group");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (!acts) return <PageLoader />;

    const toggle = (a) => {
        setSelected((s) => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; });
    };

    const runMatch = async () => {
        if (selected.size === 0) { toast.error("Choose at least one activity."); return; }
        if (windows.length === 0) { toast.error("Add at least one time window."); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/find", { activity_ids: [...selected], windows, radius });
            if (data.joined && data.outing_id) { toast.success("You've joined a group! 🎉"); nav(`/outings/${data.outing_id}`); return; }
            setResult(data);
            setView("result");
            if (data.matched) { await loadGroup(data.group_id); setView("result"); }
        } catch (e) {
            if (!handleCap(e)) toast.error(formatApiError(e.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const addWindow = (w) => setWindows((x) => [...x, w]);
    const delWindow = (w) => setWindows((x) => x.filter((y) => !(y.date === w.date && y.start === w.start && y.end === w.end)));

    const toggleRank = (aid) => {
        setRanking((r) => (r.includes(aid) ? r.filter((x) => x !== aid) : [...r, aid]));
    };

    const submitVote = async () => {
        const finalRanking = ranking.length ? ranking : group.activities.map((a) => a.id);
        await api.post(`/groups/${group.group.id}/vote`, { ranking: finalRanking });
        toast.success("Vote submitted!");
        loadGroup(group.group.id);
    };

    const confirmGroup = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/groups/${group.group.id}/confirm`);
            if (data.outing_id) {
                toast.success("Your outing is confirmed! 🎉");
                nav(`/outings/${data.outing_id}`);
            } else {
                toast.success("You're confirmed. Waiting for the group to fill up.");
                loadGroup(group.group.id);
            }
        } catch (e) { if (!handleCap(e)) toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    const capDialog = (
        <Dialog open={!!capNudge} onOpenChange={(o) => !o && setCapNudge(null)}>
            <DialogContent data-testid="outing-cap-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" /> You've hit your monthly outings</DialogTitle>
                    <DialogDescription>
                        {capNudge?.message || `Your Standard plan includes up to ${capNudge?.limit || 5} outings each month.`}
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-xl bg-secondary/50 p-4 text-sm text-muted-foreground">
                    Upgrade to <span className="font-700 text-primary">Unlimited</span> to join as many outings as you like — same club, no monthly limit.
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button onClick={() => nav("/profile?tab=membership")} data-testid="cap-upgrade-btn" className="flex-1 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">Upgrade to Unlimited</Button>
                    <Button onClick={() => setCapNudge(null)} variant="outline" data-testid="cap-dismiss-btn" className="rounded-full">Maybe later</Button>
                </div>
            </DialogContent>
        </Dialog>
    );

    // ----- GROUP VIEW -----
    if (view === "group" && group) {
        const g = group.group;
        return (
            <div className="space-y-5">
                <button onClick={() => { setView("wizard"); setStep(0); nav("/discover"); }} className="text-sm text-accent">← Back</button>
                <div className="rounded-3xl bg-card p-6 shadow-sm">
                    <div className="flex items-center gap-2 text-accent"><Vote className="h-5 w-5" /><span className="text-xs font-700 uppercase tracking-wide">{g.status}</span></div>
                    <h1 className="mt-2 font-display text-2xl font-700 text-primary">What should the group do?</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{group.confirmed_count} of {group.member_count} members confirmed. Rank your favorites, then confirm you're in.</p>
                    <div className="mt-5 space-y-2">
                        {group.activities.map((a) => {
                            const idx = ranking.indexOf(a.id);
                            return (
                                <button key={a.id} data-testid={`vote-option-${a.id}`} onClick={() => toggleRank(a.id)}
                                    className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${idx >= 0 ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                                    <span className="text-2xl">{a.emoji}</span>
                                    <span className="flex-1 font-600 text-primary">{a.name}</span>
                                    {idx >= 0 && <span className="grid h-6 w-6 place-items-center rounded-full bg-primary text-xs font-700 text-primary-foreground">{idx + 1}</span>}
                                </button>
                            );
                        })}
                    </div>
                    <Button onClick={submitVote} variant="outline" data-testid="submit-vote" className="mt-4 w-full rounded-full">Submit my vote</Button>
                </div>
                <div className="rounded-2xl bg-secondary/50 p-5">
                    <p className="text-sm text-muted-foreground">Suggested time based on the group's overlap:</p>
                    {g.suggested_windows?.slice(0, 1).map((w, i) => (
                        <p key={i} className="mt-1 font-display text-lg font-700 text-primary">{formatDate(w.date)} · {formatTime(w.start)} – {formatTime(w.end)}</p>
                    ))}
                </div>
                <Button onClick={confirmGroup} disabled={loading} data-testid="confirm-group" className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground py-6 text-base">
                    <Check className="mr-2 h-5 w-5" /> Confirm I'm in
                </Button>
                {capDialog}
            </div>
        );
    }

    // ----- RESULT VIEW -----
    if (view === "result") {
        if (!result?.matched) {
            return (
                <div className="flex flex-col items-center py-16 text-center">
                    <div className="grid h-20 w-20 place-items-center rounded-full bg-secondary text-primary"><Frown className="h-9 w-9" /></div>
                    <h1 className="mt-6 font-display text-2xl font-700 text-primary">No group just yet</h1>
                    <p className="mt-2 max-w-sm text-muted-foreground">We couldn't find enough compatible members for those choices right now. Try adding more activities, widening your travel radius, or picking different dates.</p>
                    <Button onClick={() => { setView("wizard"); setStep(0); }} className="mt-6 rounded-full bg-primary hover:bg-primary/90">Try again</Button>
                </div>
            );
        }
        return (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center">
                <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-accent/15 text-accent"><PartyPopper className="h-10 w-10" /></div>
                <h1 className="mt-6 font-display text-4xl font-700 tracking-tight text-primary">Lucky you! 🎉</h1>
                <p className="mx-auto mt-3 max-w-md text-muted-foreground">
                    We found <span className="font-700 text-primary">{result.count} other verified members</span> who are available around the same time and interested in some of the same activities.
                </p>
                <div className="mx-auto mt-8 max-w-sm rounded-2xl bg-card p-6 text-left shadow-sm">
                    <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Matches found near you</p>
                    <div className="mt-3 space-y-2" data-testid="find-activity-matches">
                        {result.common_activities.map((a) => (
                            <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl bg-secondary/50 px-4 py-3">
                                <div className="flex items-center gap-3"><span className="text-2xl">{a.emoji}</span><span className="font-600 text-primary">{a.name}</span></div>
                                {a.member_count > 0 && <span className="rounded-full bg-accent/15 px-2.5 py-1 text-xs font-700 text-accent">{a.member_count} {a.member_count === 1 ? "match" : "matches"}</span>}
                            </div>
                        ))}
                    </div>
                    {result.suggested_windows?.some((w) => w.member_count > 0) && (
                        <div className="mt-4 border-t border-border pt-4" data-testid="find-window-matches">
                            <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Times that work for others</p>
                            <div className="mt-3 space-y-2">
                                {result.suggested_windows.filter((w) => w.member_count > 0).map((w, i) => (
                                    <div key={i} className="flex items-center justify-between rounded-xl bg-secondary/50 px-4 py-2 text-sm">
                                        <span className="font-600 text-primary">{formatDate(w.date)} · {formatTime(w.start)}–{formatTime(w.end)}</span>
                                        <span className="rounded-full bg-accent/15 px-2.5 py-1 text-xs font-700 text-accent">{w.member_count} available</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> You're choosing the experience — not the people. Identities stay private until you meet.</p>
                </div>
                <Button onClick={() => loadGroup(result.group_id)} data-testid="continue-to-group" className="mt-8 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-8">
                    Vote & confirm <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
            </motion.div>
        );
    }

    // ----- WIZARD VIEW -----
    const remaining = membership?.outing_cap != null ? membership.outing_cap - membership.outings_used : null;
    return (
        <div>
            <h1 className="font-display text-2xl font-700 tracking-tight text-primary">Find Something to Do</h1>
            {remaining != null && remaining > 0 && (
                <div data-testid="find-remaining-nudge"
                    className={`mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl px-4 py-3 text-sm ${remaining <= 2 ? "bg-accent/10 text-accent" : "bg-secondary text-muted-foreground"}`}>
                    <span className="font-600">
                        {remaining <= 2 ? `Heads up — only ${remaining} outing${remaining === 1 ? "" : "s"} left this month on Standard.` : `${remaining} of ${membership.outing_cap} outings left this month.`}
                    </span>
                    {remaining <= 2 && (
                        <button type="button" onClick={() => nav("/profile?tab=membership")} data-testid="find-nudge-upgrade" className="rounded-full bg-accent px-4 py-1.5 font-600 text-accent-foreground hover:bg-accent/90">Go Unlimited</button>
                    )}
                </div>
            )}
            <Progress value={((step + 1) / 3) * 100} className="mt-4 h-2" />
            <div className="mt-6">
                {step === 0 && (
                    <div>
                        <h2 className="font-display text-xl font-700 text-primary">What would you like to do?</h2>
                        <p className="mt-1 text-sm text-muted-foreground">Pick everything that sounds fun right now.</p>
                        <div className="mt-5"><ActivityPicker byCategory={acts.by_category} categories={acts.categories} mode="select" selected={selected} onToggle={toggle} /></div>
                        <Button onClick={() => setStep(1)} data-testid="find-next-0" className="mt-6 w-full rounded-full bg-primary hover:bg-primary/90">Next</Button>
                    </div>
                )}
                {step === 1 && (
                    <div>
                        <h2 className="font-display text-xl font-700 text-primary">When can you go?</h2>
                        <p className="mt-1 text-sm text-muted-foreground">These come from your availability — add more if you like.</p>
                        <div className="mt-5"><AvailabilityEditor items={windows} onAdd={addWindow} onDelete={delWindow} testidPrefix="find-avail" /></div>
                        <div className="mt-6 flex gap-3">
                            <Button variant="outline" onClick={() => setStep(0)} className="rounded-full">Back</Button>
                            <Button onClick={() => setStep(2)} data-testid="find-next-1" className="flex-1 rounded-full bg-primary hover:bg-primary/90">Next</Button>
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div>
                        <h2 className="font-display text-xl font-700 text-primary">How far would you travel?</h2>
                        <p className="mt-1 text-sm text-muted-foreground">Adjust just for this search if you'd like.</p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            {RADII.map((r) => (
                                <button key={r} data-testid={`find-radius-${r}`} onClick={() => setRadius(r)}
                                    className={`rounded-full border px-6 py-3 font-600 transition-colors ${radius === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40"}`}>{r} mi</button>
                            ))}
                        </div>
                        <div className="mt-6 flex gap-3">
                            <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">Back</Button>
                            <Button onClick={runMatch} disabled={loading} data-testid="find-run" className="flex-1 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
                                {loading ? "Finding your group…" : "Find my group"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            {capDialog}
        </div>
    );
}
